import type { FlightLeg } from '@massilia/queries'

import { cn, Popover, PopoverContent, PopoverTrigger } from '@massilia/ui'
import { Clock, HelpCircle } from 'lucide-react'

import { formatDuration } from '@/utils/format-duration'
import { formatTime } from '@/utils/format-time'

interface StopsIndicatorProps {
  /**
   * Legs of the journey for displaying layover details in the popover
   */
  legs: FlightLeg[]
  /**
   * Number of stops (0 = direct flight)
   */
  stops: number
  /**
   * Visual variant:
   * - 'badge': larger style for flight cards (default)
   * - 'compact': smaller style for journey options panel
   */
  variant?: 'badge' | 'compact'
}

/**
 * Displays flight stops information with a popover showing layover details.
 * Works on both desktop (hover) and mobile (tap) via Popover component.
 */
export function StopsIndicator({ legs, stops, variant = 'badge' }: StopsIndicatorProps) {
  const isDirect = stops === 0

  // Direct flight - no popover needed
  if (isDirect) {
    return (
      <span
        className={cn(
          'rounded-full font-medium whitespace-nowrap text-green-600',
          variant === 'badge'
            ? 'bg-green-50 px-2 py-0.5 text-xs md:px-3 md:py-1 md:text-sm'
            : 'bg-green-100 px-2 py-0.5 text-xs'
        )}
      >
        Direct
      </span>
    )
  }

  // Flight with stops - show popover with layover details
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'flex cursor-pointer items-center gap-1 rounded-full font-medium text-orange-600 transition-colors hover:bg-orange-100',
            variant === 'badge'
              ? 'bg-orange-50 px-2 py-0.5 text-xs md:px-3 md:py-1 md:text-sm'
              : 'bg-orange-100 px-2 py-0.5 text-xs'
          )}
          onClick={(e) => {
            // Stop propagation to prevent triggering parent click handlers
            // (e.g., selecting an option in JourneyOptionsPanel)
            e.stopPropagation()
          }}
          type="button"
        >
          <span className="whitespace-nowrap">
            {stops} escale{stops > 1 ? 's' : ''}
          </span>
          <HelpCircle
            className={cn(
              'text-orange-500',
              variant === 'badge' ? 'h-3 w-3 md:h-3.5 md:w-3.5' : 'h-3 w-3'
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        className="w-auto max-w-[220px] border-none bg-orange-500 p-2 text-white shadow-lg"
        side="top"
        sideOffset={6}
      >
        <div className="space-y-1.5">
          {legs.map((leg, index) => (
            <div key={index}>
              <div className="text-xs font-medium">
                {leg.departureCity} ({leg.departureAirport}) &rarr; {leg.arrivalCity} (
                {leg.arrivalAirport})
              </div>
              <div className="text-[11px] text-orange-100">
                {formatTime(leg.departureTime)} - {formatTime(leg.arrivalTime)}
              </div>
              {leg.layover && (
                <div className="mt-0.5 flex items-center gap-1 text-[11px] text-orange-200">
                  <Clock className="size-3" />
                  <span>
                    {formatDuration(leg.layover.duration)} d&apos;escale à {leg.layover.city}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
